import { observer } from "mobx-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';

import Button from "components/UI/Button/Button";
import CheckBox from "components/UI/CheckBox/CheckBox";
import { Popup } from "../Popup/Popup";
import BannerStep from "components/BannerStep/BannerStep";
import Icon from "components/UI/IconSVG/Icon";

import AppStore from "../../AppStore";
import popupStore from "../Popup/PopupStore";
import messageBoxStore from "MessageBox/MessageBoxStore";

import { getCookie, setCookie } from "utils/cookie";
import { isListenersPage } from "utils/helpers";
import { ApiError } from "client";
import { EVENTS, metricsEvent } from "metrics";

import {
  rulesList,
  TITLE_ADULT_CONFIRM,
  TITLE_LEGAL_CONFIRM,
  TITLE_POLITICAL_CONFIRM,
} from "./acceptRulesModalConstants";

import {
  SMART_CAPTCHA_KEY,
  SMART_CAPTCHA_TEST,
} from '../../smart-captcha.js';

import "./AcceptRulesModal.scss";

// TODO: вынести конфигурацию в модель площадки,
// а в будущем из модели площадки брать и React-компонент
const TWO_STEP_PLATFORMS = ['vk', 'telegram', 'yandexseo', 'googleseo'];

export const AcceptRulesModal = observer(() => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isListeners = isListenersPage(pathname);
  const marketStore = AppStore.currentMarketStore;
  const calculator = marketStore.currentCalculator;
  const { mode } = calculator;
  const [currentStep, setCurrentStep] = useState(1);

  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaVisible, setCaptchaVisible] = useState(false);

  const requiresTwoSteps = () => {
    const twoStepPlatforms = TWO_STEP_PLATFORMS.includes(calculator.market.id);
    return marketStore.isCreatingProject && twoStepPlatforms;
  };

  const getRulesList = () => {
    if (popupStore.agreedRulesRaisePrice) return rulesList.forRaisePrice;
    if (marketStore.isCreatingProject && mode.bots) return rulesList.forBotsSeo;
    if (isListeners) return rulesList.forListeners;
    return rulesList.main;
  };

  const onCaptchaSuccess = async (captchaToken) => {
    setCaptchaVisible(false);
    setCaptchaToken(captchaToken);

    try {
      const projectId = await marketStore.createNewProject(captchaToken);
      if (!projectId) {
        setCaptchaKey(captchaKey + 1);
        return;
      }

      metricsEvent(EVENTS.PROJECT_SUBMIT);

      const redirectUrl = getCookie("redirect_url");
      if (redirectUrl) {
        setCookie("redirect_url", "", { expires: -1 });
        navigate(redirectUrl);
      } else {
        navigate("/projects");
        popupStore.close();
      }

      setCurrentStep(1);
      AppStore.resetAgreed();
      AppStore.clearUploadedFile();
      marketStore.clearFieldsAfterCreateProjects();
    } catch (e) {
      setCaptchaKey(captchaKey + 1);
      if (e instanceof ApiError) {
        messageBoxStore.showError(e.message);
        return;
      } else {
        console.error("Произошла ошибка при создании проекта:", e);
      }
    }
  }

  const handleAgreeClick = async () => {
    AppStore.setAgreedWithRules(true);
    try {
      await AppStore.handleAgreeClickForLoggedInUser();
      AppStore.resetAgreed();
      popupStore.close();
    } catch (e) {
      if (e instanceof ApiError) {
        messageBoxStore.showError(e.message);
        return;
      } else {
        console.error("Произошла ошибка при соглашении с правилами сайта:", e);
      }
    }
  };

  const handleAgreeClickCreatingProject = async () => {
    marketStore.setAgreedRulesCreatingProject(true);

    if (requiresTwoSteps() && currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    if (SMART_CAPTCHA_KEY)
      return setCaptchaVisible(true);
    onCaptchaSuccess(captchaToken);
  };

  const handleRaisePriceConfirm = async () => {
    try {
      if (
        popupStore.reportDataForRaisePrice &&
        popupStore.newExtraPriceForRaisePrice
      ) {
        await popupStore.updateTaskExtraPrice(
          popupStore.reportDataForRaisePrice,
          popupStore.newExtraPriceForRaisePrice
        );
        popupStore.setReportDataForRaisePrice(null, null);
      }

      if (
        popupStore.projectDataForRaisePrice &&
        popupStore.newExtraPriceForRaiseProjectPrice
      ) {
        await popupStore.updateProjectExtraPrice(
          popupStore.projectDataForRaisePrice, 
          popupStore.newExtraPriceForRaiseProjectPrice
        );
        popupStore.setProjectDataForRaisePrice(null, null);
        popupStore.setShowModalAuctionProject(false)
      }

      AppStore.resetAgreed();
      popupStore.close();
      popupStore.setShowModalAuction(false);
      popupStore.setShowModalAuctionProject(false);
      popupStore.setAgreedRulesRaisePrice(false);
      
    } catch (e) {
      if (e instanceof ApiError) {
        messageBoxStore.showError(e.message);
        return;
      } else {
        console.error("Произошла ошибка при поднятии стоимости:", e);
      }
    }
  };

  const isStep2Valid = AppStore.legalConfirm && AppStore.adultConfirm && AppStore.politicalConfirm;

  return (
    <Popup modalType="accept-rules" title="Основные условия использования сервиса BOOSTCLICK" subtitle={popupStore.agreedRulesRaisePrice ? "Перед повышением ставки обратите внимание!" : ""} onClick={() => setCurrentStep(1)}>
      {(currentStep === 1 || !requiresTwoSteps()) && (
        <>
          <ol className="accept-rules__list">
            {getRulesList().map((item, index) => <li key={index}>{item}</li>)}
            {!popupStore.agreedRulesRaisePrice && <li> Я ознакомился(-ась) с <Link className="accept-rules__subinfo-link" to="/docs/offer.pdf" target="_blank">договором-офертой</Link> и принимаю его условия</li>}
          </ol>
          <BannerStep banner="banner-modal" />
          <div className="accept-rules__check">
            <CheckBox classes={["accept-rules__check-chose"]} title={isListeners ? "Я подтверждаю, что мой контент не нарушает законы РФ" : "С условиями ознакомлен(-а), все понимаю и принимаю"} checked={AppStore.agreedRules} onClick={() => AppStore.setAgreedRules(!AppStore.agreedRules)} />
            {!marketStore.isCreatingProject && !popupStore.agreedRulesRaisePrice &&
              <CheckBox
                classes={["accept-rules__check-chose"]}
                title={[ "С ", <Link className="accept-rules__subinfo-link" to="/docs/policy.pdf" target="_blank" > политикой конфиденциальности </Link>, " ознакомлен(-а)"]}
                checked={AppStore.agreedPrivacy}
                onClick={() => AppStore.setAgreedPrivacy(!AppStore.agreedPrivacy)}
              />
            }
          </div>
        </>
      )}

      {requiresTwoSteps() && currentStep === 2 && (
        <>
          <div className="accept-rules__check-step2">
            <CheckBox 
              classes={["accept-rules__check-step2-chose", AppStore.legalConfirm ? "selected" : ""]} 
              title={TITLE_LEGAL_CONFIRM} 
              checked={AppStore.legalConfirm} 
              onClick={() => AppStore.setLegalConfirm(!AppStore.legalConfirm)} 
            />
            <CheckBox 
              classes={["accept-rules__check-step2-chose", AppStore.adultConfirm ? "selected" : ""]} 
              title={TITLE_ADULT_CONFIRM}
              checked={AppStore.adultConfirm} 
              onClick={() => AppStore.setAdultConfirm(!AppStore.adultConfirm)} 
            />
            <CheckBox 
              classes={["accept-rules__check-step2-chose", AppStore.politicalConfirm ? "selected" : ""]} 
              title={TITLE_POLITICAL_CONFIRM}
              checked={AppStore.politicalConfirm} 
              onClick={() => AppStore.setPoliticalConfirm(!AppStore.politicalConfirm)} 
            />
          </div>
          <BannerStep banner="banner-modal-step2" />
        </>
      )}

      {!marketStore.isCreatingProject && !popupStore.agreedRulesRaisePrice && (
        <div className="accept-rules__buttons">
          <Link className="accept-rules__support" to={process.env.REACT_APP_SUPPORT_ADDRESS} target="_blank" onClick={() => { metricsEvent(EVENTS.TELEGRAM_HELP) }}>
            <Button title="Задать вопрос" />
          </Link>
          <Button title="Соглашаюсь" onClick={handleAgreeClick} disabled={!AppStore.agreedRules || !AppStore.agreedPrivacy} />
        </div>
      )}
      {marketStore.isCreatingProject && !popupStore.agreedRulesRaisePrice && (
        <div className="accept-rules__buttons">
          <Link className="accept-rules__support" to={process.env.REACT_APP_SUPPORT_ADDRESS} target="_blank" onClick={() => { metricsEvent(EVENTS.TELEGRAM_HELP) }}>
            <Button title="Задать вопрос" />
          </Link>
          <Button 
            title="Соглашаюсь" 
            onClick={handleAgreeClickCreatingProject} 
            disabled={
              requiresTwoSteps() && currentStep === 2 
                ? !isStep2Valid
                : !AppStore.agreedRules
            } 
          />
        </div>
      )}
      {popupStore.agreedRulesRaisePrice && !marketStore.isCreatingProject && (
        <div className="accept-rules__buttons">
          <Link className="accept-rules__support" to={process.env.REACT_APP_SUPPORT_ADDRESS} target="_blank" onClick={() => { metricsEvent(EVENTS.TELEGRAM_HELP) }}>
            <Button title="Задать вопрос" />
          </Link>
          <Button title="Соглашаюсь" onClick={handleRaisePriceConfirm} disabled={!AppStore.agreedRules} />
        </div>
      )}

      {marketStore.isCreatingProject && requiresTwoSteps() && (
        <div className="accept-rules__steps-indicator">
          {currentStep <= 1 ? <Icon className="accept-rules__steps-icon" sprite="another" name="accept-rules-step1" /> : <Icon className="accept-rules__steps-icon" sprite="another" name="accept-rules-success" color="#57c058" />}
          <div className={`accept-rules__step-divider ${currentStep > 1 ? 'completed' : ''}`} />
          {currentStep < 2 ? 
            <Icon className="accept-rules__steps-icon disabled" sprite="another" name="accept-rules-step2-disable" /> :
              isStep2Valid 
                ? <Icon className="accept-rules__steps-icon" sprite="another" name="accept-rules-success" color="#57c058" /> 
                : <Icon className="accept-rules__steps-icon" sprite="another" name="accept-rules-step2" />
          }
        </div>
      )}

      {SMART_CAPTCHA_KEY && <InvisibleSmartCaptcha
        key={captchaKey}
        sitekey={SMART_CAPTCHA_KEY}
        test={SMART_CAPTCHA_TEST}
        visible={captchaVisible}
        hideShield={true}
        onSuccess={onCaptchaSuccess}
        onTokenExpired={() => setCaptchaToken('')}
        onNetworkError={() => setCaptchaVisible(false)}
        onChallengeHidden={() => setCaptchaVisible(false)}
      />}

    </Popup>
  );
});
